/**
 * 이상한 방 - 게임 세션 (리듀서 ↔ 씬·입력·카메라 접착층)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조) · 4.3절(탄환 경제) · 4.4절(정답 방) · 8절(판정 지표)
 *           `이상한방-상태모델-v0.1.md` 6.1절(두 히트 경로) · 11.3절(클라이언트가 해도 되는 것)
 *
 * ## 이 파일이 존재하는 이유
 *
 * `gameState.ts`는 순수 리듀서라 씬도 시간도 만지지 않는다. 대신 **`Effect[]`로 요청만** 돌려준다.
 * 그 요청을 실제 동작으로 바꾸는 곳이 여기다. 경계를 이렇게 그은 덕분에
 * 게임 규칙 전체가 Three.js 없이 테스트되고(`gameState.test.ts`), 2주차에 서버로 옮길 때
 * 이사할 것은 리듀서뿐이며 이 파일은 클라이언트에 남는다.
 *
 * 따라서 이 파일에는 **규칙이 하나도 없어야 한다.** 탄환을 깎지 않고, 페이즈를 판단하지 않고,
 * 정답을 판정하지 않는다. 여기서 그런 판단을 한 줄이라도 하면 그 규칙은 즉시 두 곳에 생긴다.
 *
 * ## 워프는 카메라를 건드리지 않는다
 *
 * 정답 방은 문으로 걸어가는 곳이 아니라 **같은 시점에서 방만 갈아 끼우는 것**이다(4.4절).
 * 그래서 `warpTo` 효과는 `syncObjectViews`만 부르고 `iso`는 손대지 않는다.
 * 대신 두 가지가 필수다:
 *
 *  1. **0.4초 디졸브.** 즉시 컷은 그 자체가 motion transient라, 쿨타임 30초가 막으려던 깜빡비교를
 *     전환 연출이 그대로 열어 준다. 화면을 완전히 덮은 순간에 방을 바꾼다 -
 *     그 타이밍 규약과 베일은 `sessionWarp.ts`가 소유한다.
 *  2. **프록시 동기화.** 수색 진입 시 제거된 자리에 보이지 않는 타겟을 세우지 않으면
 *     '제거' 동사가 조준 자체 불가능해진다(6.1절의 빈 셀 히트).
 *
 * ## 리빌은 방을 갈아 끼우지 않는다
 *
 * 리빌의 깜빡 교차(0.35초 간격 원본↔변경본)는 **motion transient를 일부러 쓰는** 연출이다.
 * 수색 중에는 치트인 것이 리빌에서는 정답이므로(`reveal.ts` 상단 주석) 여기서는 디졸브를 쓰지 않는다.
 *
 * 뒤집는 단위는 방이 아니라 **한 건**이다. 씬은 리빌 내내 변경본으로 고정이고 포커스된 한 건만
 * 금빛 증거 표시가 뒤집는다. 그 고정을 거는 것이 이 파일이고(`startReveal`의
 * `renderRoom(state.mutated)`), 재생기·증거 표시의 배선과 "왜 방 단위로 뒤집으면 안 되는가"는
 * `sessionReveal.ts`에 있다.
 *
 * ## 이 파일에서 갈라져 나간 것
 *
 * 접착층이라는 역할 자체는 옳지만, 한 파일이 그 전부를 들고 있을 이유는 없다. 여섯이 나갔고
 * 전부 **세션 클로저가 아니라 인자만 보도록** 잘랐다 - 그래야 옮긴 것이 배선인지 규칙인지가 드러난다.
 * 규칙은 한 줄도 나가지 않았다(나갈 규칙이 애초에 없다).
 *
 *  - `sessionView.ts`    상태 → HUD가 읽을 모양(순수 함수)
 *  - `sessionAudio.ts`   소리 · 진동 · 배경음이 지나는 문 하나
 *  - `sessionWarp.ts`    디졸브 베일과 "완전히 덮인 뒤에 교체" 규약
 *  - `sessionReveal.ts`  리빌 재생기 + 증거 표시의 수명
 *  - `sessionResult.ts`  점수표 · 진행 저장 알림 · "목표를 이뤘는가"
 *  - `sessionEffects.ts` 효과 → 자막 키·발견 연출 인자·소리 이름 · 탭 결과 → 사격 대상(순수 함수). 매치 세션(M2)이 같은 것을 부른다
 *
 * 여기 남은 것은 **효과 하나를 무엇으로 바꿀지 고르는 자리**(`applyEffect` · `onPhaseEnter`)와
 * 라운드 수명 · 입력 번역이다. 셋은 서로를 순서로 붙잡고 있어서 떼어 내면 그 순서가 사라진다.
 * `sessionEffects`는 그 "무엇으로"의 **재료**만 만든다 - 어느 순서로 어디에 띄울지는 여전히 여기다.
 *
 * ## 외우기 → 수색 전환은 원본 방 위에서 돈다
 *
 * `render/transition`이 전환 시작 프레임(= 아직 원본 방)을 텍스처로 굳혀 그것만 왜곡하고,
 * **화면이 100% 먹색인 순간에** 방을 갈아 끼운다. 순서가 뒤집히면 왜곡 도중에 변경본이 비치고
 * 그 한 프레임이 곧 정답표다.
 *
 * 그래서 `warpTo`의 디졸브와 **둘 중 하나만** 돈다(`transitionOwnsWarp`). 둘 다 돌면
 * 베일이 왜곡 화면을 덮어 연출이 서로를 지운다.
 *
 * ## 페이즈 자막이 도는 동안 제한 시간은 흐르지 않는다
 *
 * 외우기 진입에 "외워라!", 수색 진입에 "누가 다녀갔다!"가 뜬다(`ui/juice.ts`의 `phase`).
 * 그 1초는 방을 볼 수 없는 시간이므로 **플레이어가 손해 볼 이유가 없다.**
 *
 * 잠그는 지점은 `update()`의 `send({ type: 'tick' })` 한 줄뿐이다(`clockHeld`). 리듀서로
 * 들어가는 시간만 막고 워프·리빌·렌더는 그대로 돌린다 - 전환 이펙트는 자막이 뜨기 전에
 * 끝나야 하고, 화면은 계속 그려져야 한다. 자막의 완료 콜백은 **반드시 한 번** 불리므로
 * (`PhaseStampOptions.onDone`의 계약) "연출이 취소돼 시계가 영영 안 도는" 잠금은 생기지 않는다.
 *
 * ## 탄환 보급은 이 파일을 지나가되 여기 머물지 않는다
 *
 * 탄창은 **계정 단위 자원**이다(`game/magazines.ts`). 방에 놓인 물건도, 정답 방의 보상도 아니다.
 * 그래서 세션이 하는 일은 셋뿐이고 전부 배선이다:
 *
 *  1. 라운드를 세울 때 계정 보유량을 **받아서** 리듀서에 넘긴다(`deps.magazines`).
 *  2. HUD의 탄창 버튼을 `{ type: 'useMagazine' }` 이벤트로 옮긴다. **거부 판단을 하지 않는다** -
 *     쓸 수 있는지는 `gameState.canUseMagazine`이 정하고, 화면 쪽 설명은 HUD가 뷰를 보고 띄운다.
 *  3. 실제로 하나가 줄었을 때(`magazineUsed` 효과) 바깥에 알린다(`deps.onMagazineUsed`).
 *     저장은 여기서 하지 않는다 - `localStorage`를 아는 순간 이 파일이 서버로 못 옮겨 간다.
 *
 * 소비를 **효과로 알리고 상태를 훑지 않는** 이유는 순서다. 매 프레임 `state.magazines`를 감시하면
 * 라운드 교체(`retry`)로 값이 이어지는 순간과 실제 소비를 구별할 수 없다.
 *
 * ## 단계는 세션의 **맥락**이지 상태가 아니다
 *
 * 싱글이 10단계 튜토리얼이 되면서 세션은 `level`을 선택적으로 받는다. 하지만 여기서 하는 일은
 * 셋뿐이다: 결과 화면에 단계 얼굴을 씌우고, 방 생성에 목표 개수를 넘기고, 라운드가 끝났음을 알린다.
 *
 * **해금·진행 저장·다음 단계 선택은 하지 않는다.** 그건 앱 흐름(`main.ts`)의 일이고,
 * 여기서 하면 세션이 `localStorage`를 알게 되어 서버 권위로 옮길 때 같이 딸려 온다.
 * 클리어 판정도 계산하지 않고 `levels.meetsClearCondition` 하나를 부른다 - 판정식이 두 벌이 되면
 * "클리어라고 떴는데 다음 단계가 안 열린다"가 나오고, 그건 재현조차 어렵다.
 * 그 호출은 `sessionResult.ts` 한 곳에 모여 있다(세 질문이 미묘하게 갈리는 자리라서 그렇다).
 *
 * 단계가 바뀌면 세션을 **새로 만든다**(평면도·바운즈가 함께 바뀌므로 어차피 무대를 다시 세운다).
 * 그래서 `level`은 생성 시점에 한 번 받고 세터가 없다.
 *
 * ## 효과를 소리와 연출로 바꾸는 곳이 여기다 (2026-09-02)
 *
 * 리듀서는 "언제 울릴지"만 정하고(`Effect.playSound`) 무엇이 어떻게 울리는지는 모른다.
 * 그 마지막 한 칸을 여기서 잇는다. 규칙이 아니라 **번역**이므로 이 파일의 원칙을 깨지 않는다.
 *
 * 세 갈래다:
 *
 *  1. **소리** - `SOUND_FOR_GAME_ID` 표로 이름을 옮기고 엔진에 넘긴다. 발견음만 콤보 단계를
 *     함께 넘겨 **반음씩 올라간다**. 그 값은 상태에서 읽는다(`comboPitchStep(currentCombo)`) -
 *     효과가 적용되는 시점의 `state`는 이미 리듀서가 올려 둔 새 상태다.
 *  2. **연출** - 맞은 지점에 자막 스탬프를 세운다(`ui/juice.ts`). 지점을 아는 것은
 *     `shootAt`뿐이므로 마지막 탭 좌표를 붙잡아 두고 효과가 그것을 읽는다.
 *  3. **라운드의 끝소리** - 표가 고르지 못하는 유일한 소리다. `round-over` 하나에 세 가지
 *     결말(퍼펙트·달성·미달)이 걸려 있고, 그 판단에 필요한 것(단계 기준·발견 수)은
 *     리듀서가 아니라 세션이 들고 있다(`SOUND_FOR_GAME_ID` 주석이 그렇게 못박아 두었다).
 *
 * **연출이 판정을 만들지 않는다.** 자막에 찍히는 점수는 `hunt.findPointsFor`가 계산하고
 * (결과 화면의 합계와 같은 함수), 배수는 `combo.ts`가 준다. 여기서 다시 곱하면 화면의 숫자와
 * 점수판이 갈리고, 그때 사람은 점수판 전체를 믿지 않게 된다.
 *
 * ## 재도전은 방을 다시 굽지 않는다
 *
 * `retry`는 같은 방에 새 변화를 얹는다(`gameState.ts`의 시드 분리). 리듀서가 `original`을
 * 재사용하므로 **씬 갱신이 참조 비교에 걸린다** - `renderRoom`은 같은 참조면 건너뛰기 때문이다.
 * 외우기 진입에서 `original`로, 수색 진입에서 새 `mutated`로 두 번 diff가 돌고,
 * 히트 프록시는 외우기 진입에서 통째로 버려진 뒤 새 변화로 다시 선다. 이 순서가 무너지면
 * 지난 시도의 제거 프록시가 남아 **없는 물건 자리가 정답으로 잡힌다.**
 */

import type { SoundName } from '../audio/sounds'
import { prefersReducedMotion } from '../core/env'
import type { FloorPlan } from '../core/floorPlan'
import type { Catalog } from '../core/room'
import { MS_PER_SECOND } from '../core/time'
import type { RoomBounds, RoomState } from '../core/types'
import { t } from '../i18n'
import type { IsoCamera } from '../input/isoCamera'
import { pickAt } from '../input/tapPicker'
import { resetHitProxies, syncHitProxies } from '../render/hitProxy'
import { syncObjectViews } from '../render/objectView'
import type { SceneCtx } from '../render/scene'
import { createRoomTransition, type RoomTransition } from '../render/transition'
import { HUNT_CURSOR, createGameHud, type NoticeTone, type ShotMarkKind } from '../ui/gameHud'
import { createJuice, type JuiceHandle } from '../ui/juice'
import { generateChanges } from './changeGen'
import { comboPitchStep } from './combo'
import {
  createGame,
  currentCombo,
  hasRaised,
  isPerfectRound,
  reduce,
  type Effect,
  type GameDeps,
  type GameEvent,
  type GameState,
  type SoundId,
} from './gameState'
import {
  canShoot,
  computeScore,
  computeScoreBreakdown,
  resolveShot,
  toHitTarget,
  type HitTarget,
} from './hunt'
import type { LevelDef } from './levels'
import { createSessionAudio, type SoundSink } from './sessionAudio'
import { hitJuiceArgs, huntStampDelayMs, noticeForEffect, raisedSoundName, soundNameFor, toShotResult } from './sessionEffects'
import { createSessionResult, type ResultPinsView, type RoundOutcome, type SessionResult } from './sessionResult'
import { createSessionReveal, type SessionReveal } from './sessionReveal'
import { createWarpVeil, startWarpOver, type WarpVeil } from './sessionWarp'
import { buildDebugInfo, buildHudView, type SessionDebugInfo } from './sessionView'
import type { Tuning } from './tuning'

/* --- 상수 ------------------------------------------------------------------- */

/** "한 판 더"의 시드 접미사. `?seed=`로 그대로 붙여 넣어야 하므로 URL 안전 문자만 쓴다. */
const ROUND_SEED_PREFIX = '-r'

/** `<뿌리>-r<라운드>` 꼴을 되읽는다. 캡처 그룹 1 = 뿌리, 2 = 라운드 번호. */
const ROUND_SEED_PATTERN = /^(.*)-r(\d+)$/

/* --- 타입 ------------------------------------------------------------------- */

/**
 * 갈라져 나간 두 타입은 **이름을 여기서 계속 내보낸다**(`sessionAudio` · `sessionResult`).
 * 세션을 조립하는 쪽(`main.ts`)이 아는 입구는 이 파일 하나이고, 안에서 무엇이 갈라졌는지는
 * 그쪽의 관심사가 아니다 - 분할 때문에 바깥의 import 줄이 바뀌면 그건 순수 이동이 아니다.
 */
export type { SoundSink }

/**
 * 세션 하나를 세우는 데 필요한 전부.
 *
 * **선택 필드에 `| undefined`를 명시해 둔다.** `exactOptionalPropertyTypes`가 켜져 있어서
 * `x?: T`는 "없거나 T"이지 "`undefined`를 넘겨도 된다"가 아니다. 그래서 이 표시가 없으면
 * 호출부가 값을 하나 넘길 때마다 `...(v === undefined ? {} : { x: v })` 한 줄을 써야 하고,
 * 실제로 `main.ts`의 무대 조립부가 그 전개 아홉 줄로 덮여 있었다 - 무엇을 넘기는지가
 * 삼항 연산자 뒤로 숨는 코드였다. 여기 두 글자를 적으면 그쪽이 `x: request.x`로 돌아온다.
 */
export type GameSessionDeps = Readonly<{
  ctx: SceneCtx
  iso: IsoCamera
  bounds: RoomBounds
  catalog: Catalog
  plan: FloorPlan
  tuning: Tuning
  /** 1라운드 **방 시드**. "한 판 더"는 여기서 결정론적으로 파생된다. */
  seed: string
  /**
   * 시작 시도 번호(0부터). 방은 이 값과 무관하고 **변화만** 달라진다.
   *
   * `?attempt=3`이 여기로 들어온다. 특정 변화 조합에서만 나는 버그를 다시 부르는 유일한 수단이라
   * 개발 도구가 아니라 재현 수단으로 취급한다(9.4절).
   */
  startAttempt?: number | undefined
  /**
   * 라운드를 시작할 때 계정이 들고 있는 탄창 수(`game/magazines.ts`).
   *
   * **생략하면 0이다.** 세션이 이 값을 만들지 않는 이유는 소유권이다 - 계정 저장을 아는 것은
   * 앱 흐름(`main.ts`)이고, 여기서 기본값을 지어내면 저장이 비어 있을 때 공짜 탄창이 생긴다.
   * 실패의 방향을 "탄창이 없는 판" 쪽으로 고정한다(`gameState.createGame`과 같은 규약).
   */
  magazines?: number | undefined
  /**
   * 탄창 하나가 실제로 소모됐다. 인자는 **소모 뒤 남은 수**(`Effect.magazineUsed.remaining`).
   *
   * 받는 쪽이 할 일은 계정 저장을 맞추는 것 하나다. 세션이 직접 저장하지 않는 이유는
   * `localStorage`를 아는 순간 이 파일이 서버 권위로 옮겨 갈 때 같이 딸려 오기 때문이다(11.3절).
   */
  onMagazineUsed?: ((remaining: number) => void) | undefined
  /** HUD·결과 화면을 붙일 DOM 컨테이너. 보통 `document.body`. */
  mount: HTMLElement
  /** 게임 모드로 시작할지. 배치 모드로 시작하는 앱은 `false`로 두고 나중에 켠다. */
  startActive?: boolean | undefined
  /**
   * 라운드 시드가 바뀌었을 때 알린다. 주소창을 새 시드로 갈아 끼우면 새로고침 한 번에
   * 같은 방이 다시 나온다 - 버그를 만난 판을 잃지 않는 유일한 방법이다.
   * 세션이 `window`를 직접 만지지 않는 이유는 테스트 가능성이다.
   */
  onSeedChange?: ((seed: string) => void) | undefined
  /**
   * 시도 번호가 바뀌었을 때 알린다(재도전). 주소창에 `?attempt=`를 남겨 두면
   * **새로고침 한 번에 방금 그 변화 조합이 다시 나온다** - 시드만으로는 재현되지 않는 절반이다.
   */
  onAttemptChange?: ((attempt: number) => void) | undefined
  /** 소리 알림(관측용). 생략해도 소리는 그대로 난다 - 재생은 엔진의 몫이다(`SoundSink`). */
  onSound?: SoundSink | undefined

  /**
   * 이번 라운드가 속한 튜토리얼 단계. 생략하면 **자유 모드**다(결과 화면이 "한 판 더"를 띄운다).
   * 세션은 이 값으로 판정하지 않는다 - 클리어 여부는 `levels.meetsClearCondition`에 묻는다.
   */
  level?: LevelDef | null | undefined
  /** 목표 오브젝트 수. 단계의 `objectTarget`이 여기로 들어와 방 생성의 상한이 된다. */
  targetObjectCount?: number | undefined
  /** 다음 단계가 존재하는가. 결과 화면의 "다음 단계" 버튼을 만들지 말지를 가른다. */
  hasNextLevel?: boolean | undefined
  /**
   * 이 세션의 방을 **고정한다**(매일의 방, 로드맵 M9).
   *
   * 참이면 "한 판 더"가 새 시드를 파생시키지 않고 **같은 시드로 다시 세우며**(`restart`가 시도 번호를
   * 0으로 되돌리므로 같은 방·같은 이상한 곳이다), "다른 변화로 다시" 버튼은 아예 만들어지지 않는다.
   *
   * 근거: 매일의 방의 열쇠는 시드가 아니라 **날짜**다(`game/daily.dailySeed` · `DAILY_ATTEMPT` 고정).
   * 기본값(거짓)에서 `playAgain`은 `daily:2026-09-06-r1`을, `retry`는 `attempt 1`을 만드는데 둘 다
   * 날짜가 낼 수 없는 값이라, 카드가 말한 "오늘은 모두 같은 방"이 그 버튼 하나로 거짓이 된다.
   */
  lockedRoom?: boolean | undefined
  /**
   * 라운드가 끝났다(결과 페이즈 진입). **라운드당 정확히 한 번** 불린다 -
   * 진행 저장이 `plays`를 세므로 두 번 불리면 시도 횟수가 조용히 부풀어 오른다.
   */
  onRoundEnd?: ((outcome: RoundOutcome) => void) | undefined
  /** 결과 화면의 "다음 단계". 없으면 그 버튼이 만들어지지 않는다. */
  onNextLevel?: (() => void) | undefined
  /** 결과 화면의 "단계 선택". 단계 모드의 유일한 출구다. */
  onExitToSelect?: (() => void) | undefined
  /** 결과 화면의 핀 줄. 그대로 `sessionResult.ts`로 넘긴다(그쪽 주석에 조회 함수인 이유가 있다). */
  resultPins?: (() => ResultPinsView | null) | undefined
}>

/**
 * 한 라운드의 결말(`sessionResult.ts`). **이름은 여기서 계속 내보낸다** -
 * `SoundSink`와 같은 이유로, 분할 때문에 바깥의 import 줄이 바뀌면 순수 이동이 아니다.
 */
export type { ResultPinsView, RoundOutcome }

export type GameSession = Readonly<{
  getState: () => GameState
  /**
   * 개발 훅·배치 HUD가 읽는 숫자(`sessionView.SessionDebugInfo`). 매치 세션과 **같은 모양**으로 답한다 -
   * 무대가 어느 세션을 세웠는지 개발 훅이 묻지 않게(`app/stageSession.ts`). `getState`는 싱글 전용 원본으로 남는다.
   */
  getDebugInfo: () => SessionDebugInfo
  /**
   * 지금 **화면에 그려져 있는** 방. 배치 모드가 이어받을 때 쓴다 -
   * 언제나 `mutated`를 돌려주면 정답 방에 선 채로 배치 모드에 들어갔을 때
   * 화면과 편집 대상이 서로 다른 방이 된다.
   */
  getVisibleRoom: () => RoomState
  /** 한 프레임 진행(초). 배치 모드에서는 라운드가 멈춘다. */
  update: (dtSeconds: number) => void
  /** 탭 = 지목(6.1절). 클라이언트 좌표. */
  shootAt: (clientX: number, clientY: number) => void
  toggleOrigin: () => void
  /**
   * 탄창 하나로 재장전한다. HUD 칩과 같은 경로이며 거부 판단은 여기서 하지 않는다.
   *
   * 바깥으로 내주는 이유는 **개발 훅 하나뿐이다**(`__weirdRoom.useMagazine()`).
   * 배경 탭·임베드에서는 포인터가 칩에 닿지 않아 이 경로가 없으면 자동 검증이 불가능하다.
   * 사람이 쓰는 입구는 칩 하나로 남긴다 - 거부 이유를 말할 수 있는 것이 그쪽뿐이라
   * 두 번째 입구를 열면 "눌렀는데 아무 일도 없는" 순간이 생긴다
   * (`app/inputLayer.ts`의 키 상수 아래, 재장전에 단축키를 주지 않은 근거).
   */
  useMagazine: () => void
  skipPhase: () => void
  /**
   * HUD 토스트 한 줄. **이 세션의 사건이 아닌 것을 앱 흐름이 띄우기 위한 통로다**
   * (진행 저장 실패·에셋 폴백처럼 `main.ts`만 아는 일).
   *
   * 세션이 자기 HUD를 소유하므로 통로가 여기 나 있다. 바깥에서 두 번째 토스트 레이어를
   * 세우지 않는 이유는 자리다 - 같은 구석에 겹쳐 뜨면 둘 다 안 읽히고, 관찰 게임에서
   * 겹친 자막의 면적은 곧 못 본 방의 넓이다(원칙 2).
   *
   * **규칙은 지나가지 않는다.** 문구는 부르는 쪽이 사전에서 골라 넘긴다.
   */
  showNotice: (text: string, tone?: NoticeTone) => void
  /**
   * 새 라운드. 시드를 생략하면 현재 시드를 그대로 다시 쓴다.
   * **시도 번호가 0으로 돌아가므로 변화까지 첫 판과 같아진다** - 특정 라운드를 그대로
   * 재현할 때만 쓴다. 사람이 누르는 "다시 하기"는 `retry`다.
   */
  restart: (seed?: string) => void
  /**
   * "다른 변화로 다시" - **같은 방, 새 변화.** 결과 화면의 재도전 버튼이 이것이다.
   * 방을 외운 것은 살리고 리빌에서 본 정답표만 버린다.
   */
  retry: () => void
  /** "한 판 더" - 새 시드로 재시작한다. 이 프로젝트의 측정 장치다(컨셉시트 8절). */
  playAgain: () => void
  setActive: (active: boolean) => void
  isActive: () => boolean
  dispose: () => void
}>

/* --- 순수 헬퍼 --------------------------------------------------------------- */

/**
 * 시드를 뿌리와 라운드 번호로 되읽는다.
 *
 * "한 판 더"는 주소창의 시드를 갈아 끼우므로(`onSeedChange`), 3판째에 새로고침하면 이 세션의
 * 시작 시드가 `weird-room-01-r3`이 된다. 그걸 그대로 뿌리로 삼으면 다음 판이
 * `weird-room-01-r3-r1`이 되고, 새로고침을 반복할수록 시드가 계속 길어진다.
 * 되읽어 두면 그 판에서 이어서 `-r4`가 나오므로 **시드 수열이 새로고침을 건너뛰어도 이어진다.**
 *
 * 사람이 고른 시드가 우연히 `-r3`으로 끝나도 문제없다 - 이번 판의 방은 **주어진 시드 그대로**
 * 만들고(`createGame(deps.seed)`), 뿌리·라운드는 다음 판을 파생시킬 때만 쓴다.
 */
function parseRoundSeed(seed: string): { root: string; round: number } {
  const matched = ROUND_SEED_PATTERN.exec(seed)
  const root = matched?.[1]
  const round = Number(matched?.[2])

  if (root === undefined || root === '' || !Number.isSafeInteger(round)) {
    return { root: seed, round: 0 }
  }

  return { root, round }
}

/* --- 본체 ------------------------------------------------------------------- */

/**
 * 게임 세션을 만든다. HUD·결과 화면을 붙이고 1라운드를 외우기 페이즈로 세워 둔다.
 * 정리는 반환된 핸들의 `dispose()`로만 한다(HUD·DOM·리빌 재생기가 전부 묶여 있다).
 */
export function createGameSession(deps: GameSessionDeps): GameSession {
  const { ctx, iso, bounds, catalog, plan, tuning } = deps
  const canvas = ctx.renderer.domElement

  /**
   * 이 세션의 소리 문 하나(`sessionAudio.ts`). 소리·진동·관측 사본이 같은 자리에서 나가고,
   * 배경음의 "판당 한 곡" 잠금도 그 안에 있다. 여기서 하는 일은 **무엇을 울릴지 고르는 것**뿐이다.
   */
  const sessionAudio = createSessionAudio(deps.onSound)

  /**
   * 리듀서가 필요로 하는 바깥 세계. 한 번 만들어 계속 넘긴다(매 이벤트 재생성은 순수한 낭비다).
   *
   * `roomOptions`가 여기 있는 이유: 재시작도 리듀서를 거치므로(`restart` 이벤트),
   * 목표 개수를 세션이 들고 있으면 "첫 판만 30개, 다시 하기부터 180개"가 된다.
   */
  const gameDeps: GameDeps = {
    tuning,
    catalog,
    plan,
    roomOptions:
      deps.targetObjectCount === undefined
        ? undefined
        : { targetObjectCount: deps.targetObjectCount },
    generateChanges,
    resolveShot,
    computeScore,
  }

  const level = deps.level ?? null

  const seedParts = parseRoundSeed(deps.seed)
  const rootSeed = seedParts.root
  let round = seedParts.round
  // 이번 판의 방은 **주어진 시드 그대로** 만든다. 뿌리·라운드는 다음 판을 파생시킬 때만 쓴다.
  // 시도 번호는 방을 건드리지 않고 변화 시드에만 들어간다(`gameState.changeSeedFor`).
  let state: GameState = createGame(
    deps.seed,
    tuning,
    catalog,
    plan,
    gameDeps.roomOptions,
    deps.startAttempt,
    // 계정 보유량은 여기서 한 번 들어가고, 그 뒤로는 라운드 상태가 진실이다.
    // `retry`·`restart`는 이 값을 이어받으므로(리듀서) 판을 다시 해도 되살아나지 않는다.
    deps.magazines,
  )

  /** 지금 씬에 올라가 있는 방. `null`이면 씬을 믿을 수 없다는 뜻이다(배치 모드가 만졌을 수 있다). */
  let renderedRoom: RoomState | null = null

  /**
   * 페이즈 자막이 떠 있는 동안은 제한 시간이 흐르지 않는다. 자막 1초를 플레이어가 손해 볼 이유가 없다.
   *
   * 막는 것은 **리듀서로 들어가는 시간뿐**이다(`update`의 `tick` 한 줄). 워프·리빌·렌더까지
   * 세우면 전환 이펙트가 자막 뒤에서 얼어붙고, 무엇보다 화면이 정지 화면으로 읽힌다.
   */
  let clockHeld = false

  /**
   * 수색에 실제로 쓴 시간. 페이즈가 바뀌면 `phaseElapsedMs`가 0으로 초기화되므로
   * 전환 **직전**의 값을 여기 붙잡아 둔다. 결과 화면의 소요 시간이자 속도 보너스의 근거다.
   */
  let huntElapsedMs = 0

  let active = deps.startActive ?? true
  /** 라운드를 세울 수 없는 상태(변화 생성 실패 등). 재시작 전까지 이벤트를 받지 않는다. */
  let failure: string | null = null
  let cursor = ''

  /* --- 디졸브 베일 ---------------------------------------------------------- */

  /**
   * 원본↔변경본 교체를 감추는 먹색 디졸브(`sessionWarp.ts`).
   *
   * **HUD보다 먼저 세운다.** 베일은 `mount`의 첫 자식이어야 하고(z-index만으로는 형제 순서가
   * 정해지지 않는다), 무엇보다 `showRoom`을 콜백으로 받으므로 방을 실제로 갈아 끼우는 권한은
   * 여전히 이 파일에 있다 - 저쪽이 아는 것은 **언제** 부를지뿐이다.
   */
  const warpVeil: WarpVeil = createWarpVeil({
    mount: deps.mount,
    dissolveSeconds: tuning.warpDissolveSeconds,
    showRoom: (toOrigin) => showRoom(toOrigin),
  })

  /* --- 외우기 → 수색 전환 ------------------------------------------------------ */

  /**
   * 판마다 하나 살고 `dispose`에서 접는다. GPU 자원은 첫 재생에서 잡고 끝나면 놓으므로
   * 수색 5분 내내 풀스크린 버퍼를 쥐고 있지 않는다(`render/transition.ts`).
   *
   * 시안을 바꾸려면 그 파일의 `DEFAULT_TRANSITION_KIND` 하나면 된다.
   */
  const transition: RoomTransition = createRoomTransition(ctx)

  /**
   * 이번 수색 진입의 방 교체를 전환이 가져갔는가. 뒤따라오는 `warpTo` **한 번만** 소비한다.
   *
   * `enterHunt`가 `phaseEnter` 바로 뒤에 `warpTo`를 붙이므로(`gameState.ts`) 이 깃발은
   * 정확히 그 효과 하나만 먹는다. 전환 도중에 정답 방을 토글하는 경로는 평소대로 디졸브를 탄다.
   */
  let transitionOwnsWarp = false

  /* --- 씬 반영 -------------------------------------------------------------- */

  /**
   * 방을 화면에 올린다. **전체 재생성이 아니라 diff 동기화다**(`objectView.syncObjectViews`).
   * 원본과 변경본은 안 바뀐 오브젝트의 참조를 공유하므로, 워프 한 번에 실제로 갱신되는 뷰는
   * 변화 12개분뿐이다 - 391개짜리 방에서 이 차이가 곧 프레임 예산이다.
   */
  function renderRoom(room: RoomState): void {
    if (renderedRoom === room) return
    syncObjectViews(ctx, room, catalog)
    renderedRoom = room
  }

  function showRoom(toOrigin: boolean): void {
    renderRoom(toOrigin ? state.original : state.mutated)
  }

  /**
   * 지금 화면에 있어야 할 방. `renderedRoom`이 신뢰를 잃었을 때(배치 모드 진입) 쓰는 대체값이며,
   * **정답 방 체류 중이면 원본**이다 - 여기서 무조건 `mutated`를 돌려주면 원본 방에 선 채로
   * 배치 모드로 넘어갔을 때 화면과 편집 대상이 서로 다른 방이 된다.
   */
  function expectedRoom(): RoomState {
    return state.isInOriginRoom ? state.original : state.mutated
  }

  /**
   * 원본↔변경본 전환을 시작한다. 디졸브 자체는 `warpVeil`이 돌리고, 여기서 하는 일은
   * **전환 이펙트와의 조정** 하나다.
   *
   * @param instant 디졸브 없이 즉시 교체한다. 리빌의 깜빡 교차 전용이며,
   *                거기서는 motion transient가 버그가 아니라 연출이다.
   */
  function startWarp(toOrigin: boolean, instant: boolean): void {
    // 전환이 아직 돌고 있다면 접고 디졸브를 건다. 왜 그 순서인지는 `sessionWarp.startWarpOver`에 있다(여럿이 세션과 같은 함수).
    startWarpOver(transition, warpVeil, toOrigin, instant)
  }

  /* --- HUD --------------------------------------------------------------- */

  const hud = createGameHud({
    mount: deps.mount,
    handlers: {
      onToggleOrigin: () => toggleOrigin(),
      onSkipPhase: () => skipPhase(),
      onUseMagazine: () => useMagazine(),
      onRaise: () => raise(),
    },
    warpDissolveMs: tuning.warpDissolveSeconds * MS_PER_SECOND,
  })

  /**
   * 연출 레이어. **HUD를 흔들고 캔버스는 건드리지 않는다** -
   * 씬은 Three.js이고, 캔버스에 CSS transform을 걸면 렌더 해상도와 픽셀이 어긋난다.
   * 무엇보다 방이 흔들리면 관찰이 방해받는다(원칙 2).
   */
  const juice: JuiceHandle = createJuice({
    mount: deps.mount,
    punchTarget: hud.root,
  })

  /**
   * 마지막 탭 지점. 연출은 **맞은 자리**에서 터져야 하는데(화면 중앙은 비운다),
   * 그 좌표를 아는 것은 `shootAt`뿐이고 효과는 리듀서를 거쳐 나중에 도착한다.
   *
   * 상태가 아니라 **직전 입력의 흔적**이다. 여기서 판정에 쓰이는 일은 없다 -
   * 판정은 이미 `hunt.resolveShot`이 같은 프레임에 끝냈다.
   */
  let lastShot: Readonly<{ x: number; y: number }> = { x: 0, y: 0 }

  /**
   * 이번 한 발이 콤보를 끊었는가. `comboBroken` 효과가 세우고 `shootAt`이 읽어 지운다.
   *
   * 효과 안에서 곧바로 연출하지 않는 이유는 **오답 연출이 하나여야** 하기 때문이다 -
   * 끊긴 오답과 안 끊긴 오답에 각각 `juice.miss`를 부르면, 끊긴 발에서는 흔들림이 두 번 걸린다.
   */
  let comboBrokenThisShot = false

  /**
   * 점수표와 라운드 결말(`sessionResult.ts`). **판정식은 저쪽에 하나뿐이다** -
   * 여기서 `meetsClearCondition`을 다시 부르면 "클리어라고 떴는데 다음 단계가 안 열린다"가 난다.
   */
  const result: SessionResult = createSessionResult({
    mount: deps.mount,
    tuning,
    level,
    hasNextLevel: deps.hasNextLevel,
    onPlayAgain: () => playAgain(),
    /*
     * 방이 고정된 판(매일의 방)에는 재도전 버튼이 없다. 그 버튼이 주는 것은 "같은 방, **다른** 이상한 곳"인데
     * 그건 그날의 방이 아니다. 핸들러를 안 넘기면 버튼도 만들어지지 않는다(`resultScreen.retryAction`).
     */
    ...(deps.lockedRoom === true ? {} : { onRetrySameSeed: () => retry() }),
    onNextLevel: deps.onNextLevel,
    onExitToSelect: deps.onExitToSelect,
    onRoundEnd: deps.onRoundEnd,
    resultPins: deps.resultPins,
  })

  /** 커서는 "지금 쏠 수 있는가"의 유일한 상시 신호다. 값이 같으면 쓰지 않는다(매 프레임 도는 경로). */
  function syncCursor(): void {
    const next = !active || state.phase !== 'hunt' ? '' : state.isInOriginRoom ? HUNT_CURSOR.blocked : HUNT_CURSOR.aim
    if (cursor === next) return
    cursor = next
    canvas.style.cursor = next
  }

  function render(): void {
    hud.render(buildHudView(state, tuning))
    syncCursor()
  }

  /* --- 리빌 --------------------------------------------------------------- */

  /**
   * 리빌 재생기 + 증거 표시의 수명(`sessionReveal.ts`). 여기서 넘기는 것은 배선 넷뿐이고,
   * "무엇을 어떤 순서로 보여 줄지"는 `game/reveal.ts`가 이미 정해 두었다.
   */
  const reveal: SessionReveal = createSessionReveal({
    ctx,
    iso,
    catalog,
    plan,
    secondsPerStep: tuning.revealSecondsPerChange,
    showLabel: (text, tone) => {
      hud.showNotice(text, tone)
    },
    playStepSound: () => {
      sessionAudio.play('reveal')
    },
    onFinished: () => {
      // 발견분까지 다 돌았으면 타이머를 기다리지 않는다. 남는 초는 전부 손해다(8절).
      if (state.phase === 'reveal') skipPhase()
    },
  })

  /**
   * 리빌을 세운다. 재생기 쪽은 `reveal.start`가 하고, 여기 남은 것은 **다른 연출을 걷어
   * 리빌에게 화면을 내주는 일**이다 - 순서가 규약이라 한자리에 모아 둔다.
   */
  function startReveal(): void {
    /*
     * 수색이 0.6초 안에 끝나는 경로가 있다(`skipPhase`). 전환이 화면을 덮은 채로 리빌이
     * 시작되면 깜빡 교차가 통째로 가려진다. `finish()`는 대기 중인 방 교체를 반드시 실행하므로
     * 여기서 접어도 수색이 원본 방에 갇히지 않는다.
     */
    transition.finish()

    // 진행 중이던 디졸브를 접는다. 깜빡 교차와 겹치면 베일이 화면을 덮은 채로 방이 뒤집힌다.
    warpVeil.cancel()
    // 씬은 여기서부터 리빌이 끝날 때까지 **변경본으로 고정**이다.
    renderRoom(state.mutated)

    /*
     * 움직임 줄이기를 **여기서 그때그때 묻는다.** 세션을 세울 때 한 번 읽어 두면 OS 설정을
     * 켜도 새로고침 전까지 안 먹는다(`prefersReducedMotion`의 "캐시하지 않는다").
     */
    reveal.start(state, prefersReducedMotion())
  }

  function showResult(): void {
    /*
     * 정상 경로에서는 `onPhaseEnter`의 `reveal.stop()`이 이미 씬을 되돌려 두었다.
     * 이 줄이 지키는 것은 **리빌 도중에 터진 치명적 오류**다(`onFatal`이 여기로 온다) -
     * 그때 하이라이트는 아직 살아 있고, 포커스된 한 건이 원본 모습으로 선 채
     * 금빛 브래킷까지 남아 결과 화면 배경에 정답 하나가 계속 표시된다.
     * `clear()`는 어디서 불러도 안전하고, 이미 걷힌 뒤라면 아무 일도 하지 않는다.
     */
    reveal.clearHighlight()
    renderRoom(state.mutated)

    result.show(state, huntElapsedMs, reveal.steps())
  }

  /* --- 효과 처리 ----------------------------------------------------------- */

  /**
   * 새 라운드의 첫 화면으로 카메라를 되돌린다. **외우기 페이즈 진입 때만** 부른다.
   *
   * 리빌은 마지막 변화에 바짝 붙은 채로 끝나고, 워프는 설계상 카메라를 건드리지 않는다.
   * 그래서 아무것도 하지 않으면 "한 판 더"를 누른 다음 판이 **소파 뒷면 클로즈업에서 시작한다** -
   * 실제로 그렇게 시작해 봤고, 새 방을 외워야 하는 60초를 어디인지 찾는 데 먼저 쓰게 된다.
   *
   * 수색 진입에서는 절대 부르지 않는다. 외우기에서 만든 기준 기억은 **시점과 함께** 만들어지므로,
   * 수색을 다른 각도에서 시작하면 그 60초가 통째로 값을 잃는다.
   */
  function resetCamera(): void {
    iso.resetView()
  }

  /**
   * "외워라!" 한 장을 띄우고 그동안 시계를 잡는다.
   *
   * **두 곳에서 부른다**: 외우기 페이즈에 새로 들어올 때(재도전·한 판 더·재시작)와
   * 세션이 태어날 때. 첫 판은 `createGame`이 만든 상태로 시작하므로 `phaseEnter`가 없고,
   * 그래서 여기를 함수로 빼지 않으면 **판의 첫 외우기에만 자막이 안 뜬다** -
   * 가장 많이 보게 되는 화면이 정확히 그 화면이다.
   *
   * `onDone`은 **반드시 정확히 한 번** 불린다(탭으로 걷어도, 레이어가 꺼져 있어 자막이 안 떠도).
   * 그래서 "연출이 취소돼 시계가 영영 안 도는" 잠금은 여기서 방어하지 않아도 된다.
   */
  function showWatchStamp(): void {
    clockHeld = true
    juice.phase('watch', {
      onDone: () => {
        clockHeld = false
      },
    })
  }

  function onPhaseEnter(effect: Extract<Effect, { type: 'phaseEnter' }>, before: GameState): void {
    /*
     * 수색이 끝나는 순간의 경과를 붙잡는다. 이 한 줄을 빠뜨리면 결과 화면의 소요 시간이 항상 0이 된다
     * (페이즈가 바뀌면 `phaseElapsedMs`는 0으로 초기화된다).
     *
     * `before`는 이 이벤트를 넣기 **전**의 상태이므로, 시간 초과로 끝난 경우 최대 한 프레임(≈16ms)
     * 짧게 잡힌다. 표시용 값이고 점수(속도 보너스)는 발견 시각으로 따로 계산하므로 문제되지 않는다.
     */
    if (effect.previous === 'hunt') huntElapsedMs = before.phaseElapsedMs

    /*
     * 전량 발견의 자막판. **수색이 끝나는 순간에 띄운다** - 리빌 진입이 그 순간이다.
     *
     * 결과 페이즈까지 미루지 않는 이유는 겹침이다. 결과 자막판(z-index 30)이 연출 레이어(25)를
     * 통째로 덮으므로 거기서 띄우면 아무도 보지 못한다. 무엇보다 "전부 찾았다"의 감정은
     * 마지막 한 개를 맞힌 직후에 있지, 점수표 앞에 있지 않다.
     *
     * 전량 발견이 아니면 띄우지 않는다. 이 판은 "전부 찾았다 / 퍼펙트"만 말할 줄 알고
     * (`juice.finale`), 8개를 찾은 판에 그 말을 띄우면 그건 거짓말이다.
     *
     * **판단은 `before`가 아니라 `state`로 한다.** 마지막 한 개를 맞히면 리듀서가 발견 기록과
     * 리빌 진입을 **한 걸음에** 처리하므로(`onHit`이 `enterReveal`을 이어 붙인다),
     * 직전 상태의 발견 수는 언제나 하나 모자란다. 실제로 그렇게 짰다가 퍼펙트 판에서도
     * 자막판이 뜨지 않았다 - 조건은 참이 될 수 없는데 코드는 멀쩡해 보이는 종류의 버그다.
     */
    if (effect.phase === 'reveal' && state.changes.length > 0 && state.found.length >= state.changes.length) {
      juice.finale({
        perfect: isPerfectRound(state),
        foundCount: state.found.length,
        bonus: computeScoreBreakdown(state, tuning).perfectBonus,
      })
    }

    switch (effect.phase) {
      case 'watch':
        reveal.clear()
        result.hide()
        // 지난 판의 자막이 새 라운드의 외우기 60초를 덮고 있으면 그건 그대로 못 본 방의 넓이다.
        juice.clear()
        // **순서가 규칙이다** - `clear()`가 먼저 와야 지난 판의 자막을 걷고 그 다음에 새 자막을
        // 예약한다. 반대로 두면 방금 건 예약을 `clear()`가 즉시 취소한다.
        showWatchStamp()
        resetCamera()
        /*
         * 프록시를 **통째로 버린다.** 뒤이어 오는 `syncProxies` 효과도 같은 일을 하지만
         * (외우기 페이즈는 변화가 0개라 사양 목록이 비고, 남은 것은 전부 stale로 걷힌다),
         * 재도전은 같은 방을 재사용하므로 지난 시도와 **인스턴스 id가 완전히 겹친다.**
         * 그 조건에서 차분 갱신에만 기대면, 갱신 한 번을 놓치는 순간 이번 판에 멀쩡히 있는
         * 물건 자리가 정답으로 잡힌다. `resetHitProxies`가 존재하는 이유가 정확히 이 경로다.
         */
        resetHitProxies(ctx.scene, state.original, state.mutated, state.changes, catalog)
        // 새 라운드가 섰다. 다음 결말은 다시 한 번 알려야 한다.
        result.armReport()
        /*
         * 새 판 = 새 곡. 재도전도 여기를 지나므로 같은 방을 다시 보는 판에서 곡이 바뀐다
         * (같은 방 + 같은 곡이면 두 번째 판이 첫 판의 재생처럼 들린다).
         * 잠들어 있는 무대(단계 선택의 배경)는 애초에 이 경로를 타지 않지만, 그때 소리가
         * 나면 안 되는 것이 명백하므로 조건을 적어 둔다.
         */
        if (active) sessionAudio.startBgm()
        return
      case 'hunt':
        reveal.stop()
        /*
         * **지금 씬은 아직 원본 방이다** - 교체는 뒤따르는 `warpTo`가 한다(`enterHunt`의 효과 순서).
         * 그 프레임을 텍스처로 굳혀 왜곡을 돌리고, 왜곡이 끝나는 순간(화면이 100% 먹색인 순간)
         * 아래 콜백이 방을 갈아 끼운다. **이 순서가 뒤집히면 전환 도중에 정답이 샌다.**
         *
         * 이펙트가 서지 못하면(저사양·컨텍스트 소진) `false`가 오고, 그때는 아래 `warpTo`의
         * 디졸브가 평소대로 처리한다 - 연출 실패가 판을 막지 않는다.
         */
        transitionOwnsWarp = transition.play(() => showRoom(false))
        /*
         * "누가 다녀갔다!" 한 장. 전환이 **끝난 직후**에 뜬다 -
         * 왜곡 위에 겹치면 둘 다 안 읽힌다.
         *
         * 지연 타이머는 연출 레이어가 갖는다(`delayMs`). 여기서 `setTimeout`으로 미루면
         * 라운드가 접힌 뒤에 자막이 뒤늦게 튀어나오고, 그 타이머를 걷을 사람이 없다.
         *
         * 수색 시계도 함께 잡는다. 개발자가 못박은 것은 외우기 쪽이지만 이유는 똑같고
         * (자막 1초 = 손해), 늦게 시작하는 것은 언제나 플레이어에게 유리해 공정성 문제가 없다.
         */
        clockHeld = true
        juice.phase('hunt', {
          // 화면을 덮고 있는 연출의 실제 길이(`sessionEffects.huntStampDelayMs` - 여럿이 세션과 같은 식).
          delayMs: huntStampDelayMs(transitionOwnsWarp, tuning.warpDissolveSeconds, prefersReducedMotion()),
          onDone: () => {
            clockHeld = false
          },
        })
        return
      case 'reveal':
        startReveal()
        return
      case 'result':
        reveal.stop()
        // 순서가 규약이다. 진행을 먼저 확정해야 결과 화면의 "다음 단계"가 이미 열린 단계를 가리킨다.
        result.report(state, huntElapsedMs)
        showResult()
        return
    }
  }

  /**
   * 라운드의 끝소리. **표가 고르지 못하는 유일한 소리다.**
   *
   * `SOUND_FOR_GAME_ID`는 `round-over`를 `roundClear`로 기본 매핑해 두고, 실패·퍼펙트는
   * 호출부가 고르라고 명시해 두었다(그 판단에 필요한 정보를 리듀서가 갖고 있지 않기 때문이다).
   * 퍼펙트를 먼저 보는 이유: 퍼펙트는 언제나 전량 발견이라 뒤에 두면 영원히 도달하지 않는다.
   *
   * "목표를 이뤘는가"는 `result.metItsGoal`에 묻는다 - 그 질문의 답이 결과 화면·진행 저장의
   * 답과 어떻게 갈리는지는 `sessionResult.ts` 머리말에 있다.
   */
  function roundOverSound(): SoundName {
    if (isPerfectRound(state)) return 'perfect'
    return result.metItsGoal(state) ? 'roundClear' : 'roundFail'
  }

  /**
   * 리듀서의 소리 요청을 사전의 이름으로 옮겨 실제로 울린다.
   *
   * 표에 없는 id는 **조용히 넘긴다.** 사전과 규칙을 일부러 묶어 두지 않았으므로
   * (`SOUND_FOR_GAME_ID` 주석) 규칙 쪽에 소리가 하나 늘면 그 소리만 나지 않고 게임은 그대로 돈다.
   * 표 조회는 `sessionEffects.soundNameFor`다 - 매치 세션도 같은 손으로 이름을 옮긴다.
   * 여기서 던지면 소리 사전의 누락 하나가 라운드를 멈추는 사고가 된다.
   */
  function playGameSound(id: SoundId): void {
    const name = id === 'round-over' ? roundOverSound() : soundNameFor(id)
    if (name === undefined) return

    // 발견음만 콤보를 읽는다. 나머지 소리는 이 인자를 무시하므로(`sounds.sound`) 조건이 필요 없다.
    sessionAudio.play(name, comboPitchStep(currentCombo(state)))
  }

  function applyEffect(effect: Effect, before: GameState): void {
    switch (effect.type) {
      case 'phaseEnter':
        onPhaseEnter(effect, before)
        return

      case 'warpTo':
        if (transitionOwnsWarp) {
          /*
           * 수색 진입의 교체는 전환 이펙트가 가져갔다. 여기서 베일까지 켜면 두 연출이 서로를 덮는다.
           * **한 번만 소비한다** - 전환이 도는 도중의 정답 방 토글은 평소대로 디졸브를 타야 한다.
           */
          transitionOwnsWarp = false
          return
        }
        // 리빌은 자기 타임라인으로 화면을 뒤집는다. 디졸브를 얹으면 두 연출이 서로를 덮는다.
        startWarp(effect.origin, state.phase === 'reveal')
        return

      case 'syncProxies':
        // 6.1절의 빈 셀 히트. 이게 빠지면 '제거' 동사만 조용히 조준 불가능해진다.
        syncHitProxies(ctx.scene, state.original, state.mutated, state.changes, catalog)
        return

      case 'playSound':
        playGameSound(effect.id)
        return

      /* 아래 문구는 전부 사전에서 온다(`notice.remaining` 포함). 하드코딩된 한국어는 없다. */
      case 'foundChange': {
        /*
         * 이 게임의 유일한 보상이 터지는 자리다. 자막탑(문구 · 배수 칩 · 점수)을 **맞은 지점**에
         * 세운다. 콤보는 상태에서 읽는다 - 리듀서가 방금 올려 둔 값이고(`comboUp`이 이 효과보다
         * 먼저 나온다), 여기서 다시 세면 규칙이 두 벌이 된다.
         *
         * 점수는 `hunt.findPointsFor` 하나로 계산한다. 결과 화면의 합계와 **같은 함수**여야
         * 자막의 "+240"과 총점이 맞아떨어진다. 넷을 펴는 것은 `sessionEffects.hitJuiceArgs`다.
         */
        // 걸기(M7) 여부도 상태에서 읽는다 - 리듀서가 이 발견 기록에 굳힌 것과 같은 값이라 자막의 두 배가 내역의 두 배다.
        juice.hit(lastShot.x, lastShot.y, hitJuiceArgs(currentCombo(state), hasRaised(state)))
        showNoticeFor(effect)
        return
      }

      /*
       * 콤보가 끊겼다. **여기서 연출하지 않고 표시만 남긴다** - 오답 한 발의 연출은
       * `shootAt`이 한 번만 낸다(`comboBrokenThisShot` 주석).
       */
      case 'comboBroken':
        comboBrokenThisShot = true
        return

      /*
       * 콤보가 올랐다. 소리와 자막이 이 값을 읽지만 **둘 다 상태에서 직접 읽으므로**
       * 여기서 할 일이 없다. 효과를 무시하지 않고 명시적으로 받아 두는 이유는 두 가지다 -
       * `Effect`가 하나 더 늘었을 때 컴파일러가 이 switch에서 멈춰 주고, 읽는 사람이
       * "이 효과는 어디로 갔나"를 찾아 헤매지 않는다.
       */
      case 'comboUp':
        return

      case 'lowAmmo':
        showNoticeFor(effect)
        return

      /*
       * 탄창 하나가 실제로 줄었다. 하는 일은 **바깥에 알리는 것 하나뿐**이다.
       *
       * 토스트를 띄우지 않는 이유는 HUD가 이미 말하기 때문이다 - 잔탄이 느는 것을 스스로 보고
       * 큰 숫자 위에 `+N`을 띄운다(`gameHud.ammoGainSince`). 여기서 한 줄 더 띄우면 같은 사건이
       * 화면 두 곳에서 말해지고, 자막이 겹치는 만큼 방이 가려진다(원칙 2).
       *
       * 알리는 값이 `remaining`인 것이 중요하다. 저장 쪽이 스스로 1을 빼면 뺄셈이 두 곳에 생기고,
       * 상한이나 거부 규칙이 바뀌는 날 두 숫자가 갈린다.
       */
      case 'magazineUsed':
        deps.onMagazineUsed?.(effect.remaining)
        return

      case 'originEntryDenied':
        showNoticeFor(effect)
        return

      /*
       * 걸기(M7)가 성립했다. 칩이 두 겹이 되는 것은 뷰(`GameHudView.raised`)가 말하고, 여기서는 시점을 찍는 토스트 한 줄과
       * 선언의 소리 하나다. 소리 이름은 `sessionEffects.raisedSoundName`이 고른다 - 여럿이 세션과 같은 함수다(사본을 두지 않는다).
       */
      case 'raised':
        sessionAudio.play(raisedSoundName())
        showNoticeFor(effect)
        return
    }
  }

  /**
   * 효과가 요구하는 토스트 한 줄. 키·인자·톤은 `sessionEffects.noticeForEffect`(순수)가 고르고,
   * 문장은 여기서 사전으로 만든다 - i18n은 전역 locale을 읽는 모듈이라 순수 함수 쪽에 두지 않는다.
   * `null`(자막이 필요 없는 효과)이면 아무것도 하지 않는다.
   */
  function showNoticeFor(effect: Effect): void {
    const notice = noticeForEffect(effect)
    if (notice === null) return
    hud.showNotice(t(notice.key, notice.params), notice.tone)
  }

  /**
   * 라운드를 세울 수 없게 됐다. **조용히 삼키지 않는다** -
   * 변화가 0개인 방에서 5분을 뒤지게 만드는 것이 이 프로젝트에서 가장 비싼 실패다.
   */
  function onFatal(error: unknown): void {
    failure = error instanceof Error ? error.message : String(error)
    console.error('[session] 라운드를 진행할 수 없다. "한 판 더"로 새 시드를 뽑아라.', error)
    // 예외 메시지(`failure`)는 계약 위반의 이름이라 번역 대상이 아니고, 토스트에 넣으면
    // 한 줄에 들어가지도 않는다. 사람에게 하는 말만 띄우고 원문은 위의 콘솔에 남긴다.
    hud.showNotice(t('error.bootFailed'), 'warn')

    // 결과 화면을 띄우는 이유는 점수를 보여 주려는 것이 아니라 **"한 판 더" 버튼을 내주기 위해서다.**
    // 토스트만 띄우고 끝내면 유일한 복구 수단이 새로고침이 되고, 그 순간 시드가 URL로만 남는다.
    // 여기서 또 던지면 원인 예외가 덮이므로 표시 실패는 콘솔에만 남긴다.
    try {
      showResult()
    } catch (displayError) {
      console.error('[session] 오류 결과 화면도 띄우지 못했다.', displayError)
    }
  }

  /** 이벤트를 리듀서에 넣고 결과 효과를 실제 동작으로 바꾼다. 이 파일의 유일한 상태 변경 경로다. */
  function send(event: GameEvent): void {
    // 라운드가 서지 못한 상태에서 빠져나오는 길은 새 라운드뿐이다. 재도전도 그 길에 든다 -
    // 변화 생성이 이번 시도의 조합에서만 실패했다면 다음 시도가 곧 복구다.
    if (failure !== null && event.type !== 'restart' && event.type !== 'retry') return

    const before = state

    try {
      const step = reduce(state, event, gameDeps)
      state = step.state
      for (const effect of step.effects) applyEffect(effect, before)
    } catch (error) {
      onFatal(error)
    }
  }

  /* --- 입력 --------------------------------------------------------------- */

  /**
   * 탭 한 발.
   *
   * 판정은 `hunt.resolveShot`이 하고 여기서는 **같은 순수 함수를 미리 한 번 더 불러** 표시만 고른다.
   * 상태 변화(탄환·발견 수)를 보고 역산하지 않는 이유는, 그 역산이 곧 규칙의 사본이기 때문이다 -
   * 한 곳만 바뀌면 "정답인데 빗나감으로 표시되는" 최악의 버그가 된다.
   */
  function shootAt(clientX: number, clientY: number): void {
    if (!active || failure !== null) return
    if (state.phase !== 'hunt') return

    /*
     * **방이 아직 안 바뀐 프레임의 한 발은 판정에 넣지 않는다.**
     *
     * 디졸브는 한가운데에서 방을 갈아 끼우므로(`WARP_SWAP_AT`), 그 전까지 화면에 서 있는 것은
     * **직전 방**이고 리듀서가 판정에 쓰는 것은 **이미 바뀐 상태**다. 원래 방에서 나온 직후의
     * 0.2초가 그 창이고, 거기서 이동 변화의 **옛 자리**를 쏘면 화면에는 그 물건이 그대로
     * 서 있는데 판정은 변경본 기준으로 돌아 정답이 된다 - 대조하러 다녀오는 길이 곧 정답
     * 자동 추출기가 된다(전환 이펙트가 서지 못해 수색 진입까지 디졸브가 맡는 판도 같다).
     *
     * 조용히 버리지 않는 이유는 이 저장소가 반복해서 지킨 원칙이다 - 반응이 없는 입력은
     * 고장으로 읽힌다. 자리에 이유만 띄우고 탄환·콤보는 건드리지 않는다.
     */
    if (warpVeil.isBeforeSwap()) {
      hud.flashShot(clientX, clientY, 'dry', t('hud.warpHint'))
      return
    }

    // 판정 대상은 **화면에 그려져 있는 방**이어야 한다. 정답 방에 있는 동안 `mutated`로 찍으면
    // 원본에만 있는 오브젝트가 통째로 안 잡혀 "눌렀는데 아무 일도 없다"가 된다.
    const room = renderedRoom ?? expectedRoom()
    const pick = pickAt(ctx, clientX, clientY, room, catalog, bounds, {
      mode: 'search',
      zoom: iso.getZoom(),
    })
    const target: HitTarget | null = toHitTarget(toShotResult(pick))

    const mark = markFor(target)
    // 연출은 리듀서가 효과를 내기 **전에** 지점을 알아야 한다(`lastShot` 주석).
    lastShot = { x: clientX, y: clientY }
    comboBrokenThisShot = false
    send({ type: 'shoot', hit: target })

    // 4.4절: 정답 방에서는 쏠 것이 없다. 눌린 자리에 즉시 이유를 띄운다 -
    // 아무 반응이 없으면 버튼이 고장 난 것으로 읽히고, 그 순간 규칙이 규칙으로 보이지 않는다.
    if (mark.kind === 'dry') hud.flashShot(clientX, clientY, 'dry', mark.label)
    /*
     * 정답에서는 **히트마커의 글자를 지운다.** 같은 지점에 자막 스탬프가 이미 서 있으므로
     * (`foundChange`의 `juice.hit`), 라벨을 그대로 두면 "찾았다"가 두 겹으로 겹쳐 둘 다 안 읽힌다.
     * 고리는 남긴다 - 어디를 맞혔는지 말하는 것은 글자가 아니라 그 고리다.
     * 스크린리더 안내는 `flashShot` 안에서 따로 나가므로 여기서 잃는 것은 없다.
     */
    else if (mark.kind === 'object' || mark.kind === 'removed') {
      hud.flashShot(clientX, clientY, mark.kind, '')
    } else hud.flashShot(clientX, clientY, mark.kind)

    /*
     * 오답 한 발의 연출. **가장자리 플래시는 HUD가 이미 하고 있으므로 끈다**
     * (`flashShot`의 `gh-damage`) - 같은 빨강이 두 겹이면 그만큼 방이 가려진다.
     * 여기서 더하는 것은 미세한 흔들림과, 콤보를 잃었을 때의 자막 한 장이다.
     */
    if (mark.kind === 'miss') {
      juice.miss(clientX, clientY, { comboBroken: comboBrokenThisShot, flash: false })
    }

    render()
  }

  /** 이번 한 발을 화면에 어떻게 표시할 것인가. 탄환 계산은 하지 않는다(리듀서의 몫). */
  function markFor(target: HitTarget | null): { kind: ShotMarkKind; label?: string } {
    if (state.isInOriginRoom) return { kind: 'dry', label: t('hud.originHint') }
    if (!canShoot(state)) return { kind: 'dry' }

    const outcome = resolveShot(state, target)
    if (outcome.kind === 'already-found') return { kind: 'already-found' }
    if (outcome.kind === 'miss') return { kind: 'miss' }

    return { kind: target?.kind === 'proxy' ? 'removed' : 'object' }
  }

  function toggleOrigin(): void {
    send({ type: 'toggleOrigin' })
    render()
  }

  /**
   * 탄창 하나로 재장전한다.
   *
   * **누르면 언제나 보낸다.** 못 쓰는 상황을 여기서 걸러 내면 거부 규칙이 리듀서와 여기 두 벌이
   * 되고, 둘이 갈리는 날 버튼은 "눌리는데 아무 일도 없는" 물건이 된다. 거부의 소리는 리듀서가
   * 내고(`shot-blocked`), 왜 못 쓰는지의 설명은 HUD가 마지막 뷰를 보고 띄운다.
   */
  function useMagazine(): void {
    send({ type: 'useMagazine' })
    render()
  }

  /**
   * 걸기(M7). 탄창과 같은 규약이다 - **누르면 언제나 보낸다.** 칩이 뷰로 잠금을 가르지만(`raiseChip.ts`) 그건 설명을 위한
   * 것이고 판정은 리듀서다(`gameState.onRaise` - 조건 미달이면 `shot-blocked`). 여기서 조건을 한 번 더 보면 규칙이 두 벌이다.
   */
  function raise(): void {
    send({ type: 'raise' })
    render()
  }

  function skipPhase(): void {
    send({ type: 'skipPhase' })
    render()
  }

  /* --- 라운드 수명 ---------------------------------------------------------- */

  /** 새 라운드를 세우기 전에 세션이 들고 있던 지난 판의 흔적을 지운다. `restart`·`retry` 공용. */
  function clearRoundBookkeeping(): void {
    failure = null
    huntElapsedMs = 0
    result.armReport()
    reveal.clear()
    result.hide()
    /*
     * 지난 판의 전환을 접는다. 대기 중이던 방 교체는 여기서 실행되고 곧바로 새 라운드가
     * 그 위를 덮으므로 화면에 남는 것은 없다. 접지 않으면 새 판의 외우기 화면 위에서
     * 지난 판의 왜곡이 마저 돌고, 그 왜곡이 굳혀 든 텍스처는 **지난 판의 방**이다.
     */
    transition.finish()
    transitionOwnsWarp = false
    // `juice.clear()`가 대기 중인 자막의 `onDone`을 부르므로 시계는 그 자리에서 풀린다.
    clockHeld = false
    juice.clear()
    comboBrokenThisShot = false
  }

  function restart(seed: string): void {
    clearRoundBookkeeping()

    send({ type: 'restart', seed })
    deps.onSeedChange?.(seed)
    render()
  }

  /**
   * "다른 변화로 다시" - **같은 방, 새 변화.**
   *
   * 방을 다시 굽지 않으므로(리듀서가 `original`을 재사용한다) 여기서 확인해야 하는 것은
   * **씬이 실제로 갱신되는가**이고, 그 답은 참조 비교에 걸려 있다:
   *  - `renderRoom`은 `renderedRoom === room`이면 건너뛴다. 결과 화면은 `mutated`를 그려 두므로
   *    외우기 진입의 `original`은 다른 참조 → 소품 뷰가 diff로 되돌아온다.
   *  - 수색 진입에서는 생성기가 만든 **새 `mutated`**가 오므로 또 한 번 diff가 돈다.
   *  - 프록시는 외우기 진입에서 통째로 버려지고(`onPhaseEnter`) 수색 진입에서 새 변화로 다시 선다.
   */
  function retry(): void {
    clearRoundBookkeeping()

    send({ type: 'retry' })
    deps.onAttemptChange?.(state.attempt)
    render()
  }

  /**
   * "한 판 더" - 새 시드로 재시작한다(컨셉시트 8절의 단일 판정 지표).
   *
   * **`Math.random()`을 쓰지 않는다.** 시드는 라운드 카운터로만 파생되므로
   * `weird-room-01`로 시작한 세 번째 판은 언제 어디서 돌려도 `weird-room-01-r2`와 같은 방이다.
   * `-r` 접미사를 쓴 이유는 그대로 `?seed=`에 붙여 넣을 수 있어야 하기 때문이다 -
   * `#`을 쓰면 URL 프래그먼트가 되어 시드가 잘린다.
   */
  function playAgain(): void {
    /*
     * 방이 고정됐으면 **같은 시드로 다시 세운다**(`lockedRoom`). `restart`가 시도 번호를 첫 값으로
     * 되돌리므로 방도 이상한 곳도 그대로다 - 매일의 방의 "한 판 더"는 새 방이 아니라 오늘의 방 재도전이다.
     */
    if (deps.lockedRoom === true) {
      restart(deps.seed)
      return
    }

    round += 1
    // 0판은 뿌리 그대로다(접미사가 붙으면 첫 판을 `?seed=`로 재현할 수 없게 된다).
    restart(round === 0 ? rootSeed : `${rootSeed}${ROUND_SEED_PREFIX}${round}`)
  }

  /* --- 프레임 ------------------------------------------------------------- */

  function update(dtSeconds: number): void {
    if (!active) return

    /*
     * 연출은 자막이 떠 있는 동안에도 계속 돈다. 전환 이펙트는 자막이 뜨기 **전에** 끝나야 하고,
     * 금빛 숨쉬기는 멈추면 정지 화면이 된다. 잠그는 것은 아래의 `tick` 한 줄뿐이다.
     */
    transition.update(dtSeconds)
    warpVeil.advance(dtSeconds)
    reveal.update(dtSeconds)

    /*
     * 시간은 오직 여기로만 들어간다. 리듀서는 `performance.now()`도 `Date.now()`도 읽지 않는다.
     *
     * 페이즈 자막이 떠 있는 동안은 넣지 않는다(`clockHeld`). 이 한 줄을 흘리면 자막을 띄우기만
     * 하고 외우기 60초는 그대로 깎이며, 그건 "자막이 사라진 후부터 시작"의 위반이다.
     */
    if (!clockHeld) send({ type: 'tick', dtMs: dtSeconds * MS_PER_SECOND })
    render()
  }

  function setActive(next: boolean): void {
    if (active === next) return

    /*
     * 배치 모드로 나가기 전에 **정답 방에서 먼저 쫓아낸다.**
     *
     * 배치 모드는 라운드를 멈추므로, 원본 방에 선 채로 나가면 체류 20초가 흐르지 않는다.
     * 즉 "배치 버튼을 눌러 원본을 무제한으로 들여다보기"가 성립하고, 그건 4.4절의 체류 상한과
     * 쿨타임 30초가 막으려던 것 그 자체다. 도구가 규칙에 뚫어 준 구멍은 도구의 문제가 아니라 규칙의 문제다.
     *
     * 새 규칙을 만들지 않고 **수동 퇴장과 같은 이벤트**를 보낸다 - 그래야 쿨타임도 규칙대로 걸린다.
     */
    if (!next && state.isInOriginRoom) send({ type: 'toggleOrigin' })

    active = next
    hud.setVisible(next)
    // 연출도 함께 접는다. 안 그러면 배치 모드로 나간 화면 위에 몇 초 전의 자막이 남는다.
    juice.setVisible(next)

    if (!next) {
      result.hide()
      /*
       * 배치 모드는 프레임을 멈춘다(`main.ts`의 `frame`). 전환을 세워 두면 왜곡 화면이 얼어붙고,
       * 무엇보다 **방 교체가 영영 일어나지 않는다.** `finish()`는 대기 중인 교체를 반드시 실행한다.
       *
       * **순서가 중요하다.** `finish()`가 `showRoom(false)`를 부르며 `renderedRoom`을 세우므로
       * 아래의 `renderedRoom = null`은 그 뒤여야 한다.
       */
      transition.finish()
      transitionOwnsWarp = false
      /*
       * 자막은 `juice.setVisible(false)`가 이미 걷었고 그 자리에서 `onDone`이 불렸다.
       * 그래도 명시적으로 되돌린다 - "자막은 안 떠 있는데 시계만 잠긴" 상태가 남으면
       * 돌아온 판이 통째로 멈추고, 그건 화면상 아무 단서도 남기지 않는 종류의 사고다.
       */
      clockHeld = false
      // 배치 모드가 씬을 편집한다. 다시 들어올 때 무조건 다시 그려야 하므로 신뢰를 여기서 끊는다.
      renderedRoom = null
      warpVeil.cancel()
      syncCursor()
      return
    }

    showRoom(state.isInOriginRoom)
    /*
     * 잠들어 있던 무대가 깨어났다. 아직 곡을 뽑은 적이 없을 때만 뽑는다 - 이미 돌고 있는
     * 판이라면(배치 모드를 다녀온 경우) 그 판의 곡을 그대로 이어야 한다. 여기서 무조건
     * `startBgm()`을 부르면 Tab 한 번에 곡이 갈리고, 그건 "판 도중에 안 바뀐다"의 위반이다.
     */
    if (!sessionAudio.hasStartedBgm()) sessionAudio.startBgm()
    if (state.phase === 'result') showResult()
    render()
  }

  /* --- 초기 표시 ------------------------------------------------------------ */

  hud.setVisible(active)
  juice.setVisible(active)
  /*
   * 첫 외우기의 "외워라!". `createGame`은 리듀서를 거치지 않아 `phaseEnter`가 없으므로
   * 이 한 줄이 없으면 **판의 첫 화면에만** 자막이 빠진다.
   *
   * 잠든 무대(단계 선택의 배경)에서는 연출 레이어가 꺼져 있어 자막이 생략되고, 그 자리에서
   * `onDone`이 불려 시계도 잠기지 않는다 - 애초에 프레임을 받지 않는 무대다.
   */
  showWatchStamp()
  // 첫 판의 곡. 잠든 무대(단계 선택의 배경)는 뽑지 않는다 - 판이 돌지 않는 곳에 배경음은 없다.
  if (active) sessionAudio.startBgm()
  showRoom(false)
  // 지난 세션(HMR·모드 전환)의 프록시가 남아 있으면 외우기 페이즈에 유령 타겟이 선다.
  syncHitProxies(ctx.scene, state.original, state.mutated, state.changes, catalog)
  render()

  return {
    getState: () => state,
    getDebugInfo: () => buildDebugInfo(state, tuning),
    getVisibleRoom: () => renderedRoom ?? expectedRoom(),
    update,
    shootAt,
    toggleOrigin,
    useMagazine,
    skipPhase,
    showNotice: (text: string, tone?: NoticeTone) => {
      hud.showNotice(text, tone)
    },
    restart: (seed?: string) => restart(seed ?? state.seed),
    retry,
    playAgain,
    setActive,
    isActive: () => active,

    dispose(): void {
      reveal.stop()
      /*
       * 전환의 GPU 자원(렌더타깃·쿼드·셰이더)과 `window`의 건너뛰기 리스너를 여기서 놓는다.
       * 무대는 단계를 옮길 때마다 갈리므로 이 한 줄이 없으면 풀스크린 버퍼가 단계마다 쌓인다.
       */
      transition.dispose()
      /*
       * 곡과 진동을 함께 끊는다(`sessionAudio.stop`). 무대 교체는 **먼저 버리고 나중에
       * 세우므로**(`main.ts`의 `swapStage`) 이 한 줄이 다음 판의 첫 곡보다 언제나 먼저
       * 실행된다 - 두 곡이 겹쳐 울릴 창이 없다.
       */
      sessionAudio.stop()
      juice.dispose()
      hud.dispose()
      result.dispose()
      warpVeil.dispose()
      canvas.style.cursor = ''
    },
  }
}
